import { m } from "framer-motion";
import { Link } from "react-router-dom";
import { track } from "@/lib/analytics";
import { FEATURED_CLIP } from "./featuredClip";

/**
 * The hero's worked example: a published clip as readers see it.
 *
 * Static markup rather than a live fetch — the hero must render identically for
 * every visitor and never depend on the CMS being up. But it depicts a REAL
 * published clip, so the URL in the chrome resolves and the whole panel links
 * to it. The design doc showed a `manticore.link/t/...` address, which is not a
 * domain this product serves; clips live at CLIP_URL_BASE —
 * manticscore.com/clip/<slug>.
 *
 * Every string below comes from `FEATURED_CLIP`, not from this file: the panel
 * spent a release advertising a title and source count the CMS had already
 * replaced (#277), which is what happens when depicted copy is retyped at the
 * point of use.
 */
const CLIP_SLUG = FEATURED_CLIP.slug;

export default function ClipPreviewPanel() {
  return (
    <m.div
      initial={{ opacity: 0, y: 22 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}
      className="rounded-[22px] border border-white/[0.08] bg-[#0B0B0D] p-[22px] shadow-[0_30px_80px_rgba(0,0,0,0.6)]"
    >
      <div className="flex items-center gap-[7px] pb-3.5">
        <span aria-hidden="true" className="h-1.5 w-1.5 rounded-full bg-ios-success" />
        <span className="truncate text-[11.5px] text-ios-muted">
          manticscore.com/clip/{CLIP_SLUG}
        </span>
      </div>

      <p className="ios-chip-label text-ios-muted">
        A thread of {FEATURED_CLIP.sourceCount} sources
      </p>
      <h2 className="mt-1.5 text-2xl font-extrabold text-ios">
        <Link
          to={`/clip/${CLIP_SLUG}`}
          onClick={() => track("clip_card_opened", { slug: CLIP_SLUG, location: "hero_preview" })}
          className="transition-colors hover:text-ios-accent-text"
        >
          {FEATURED_CLIP.title}
        </Link>
      </h2>
      <p className="mt-1 text-[13.5px] leading-normal text-ios-secondary">
        {FEATURED_CLIP.description}
      </p>

      <div className="mt-4 overflow-hidden rounded-[14px] bg-[#141418]">
        <div className="flex h-24 items-center justify-center bg-[linear-gradient(135deg,#101418,#2a3a50)]">
          <svg width="26" height="26" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <path
              d="M7 5a2 2 0 100-4 2 2 0 000 4z M7 23a2 2 0 100-4 2 2 0 000 4z M17 9a2 2 0 100-4 2 2 0 000 4z M7 5v14 M17 9a8 8 0 01-8 8"
              stroke="rgba(255,255,255,0.75)"
              strokeWidth="1.5"
              strokeLinecap="round"
            />
          </svg>
        </div>
        <div className="px-3.5 py-[13px]">
          <p className="text-[15px] font-bold leading-[1.3] text-ios">
            {FEATURED_CLIP.firstCard.title}
          </p>
          <p className="mt-1 text-[12.5px] leading-[1.45] text-ios-secondary">
            {FEATURED_CLIP.firstCard.summary}
          </p>
        </div>
      </div>

      <blockquote className="mt-[11px] flex gap-2.5">
        <span aria-hidden="true" className="w-[3px] shrink-0 rounded-sm bg-ios-accent" />
        <p className="font-serif text-[14.5px] italic leading-[1.45] text-ios">
          &ldquo;{FEATURED_CLIP.firstQuote.body}&rdquo;
        </p>
      </blockquote>

      <div className="mt-3 rounded-[13px] border-l-[3px] border-ios-accent bg-ios-accent/12 px-3.5 py-3">
        <p className="text-[13.5px] font-semibold text-ios">{FEATURED_CLIP.firstCta.heading}</p>
        <p className="mt-1.5 text-[12.5px] font-semibold text-ios-accent-text">
          {FEATURED_CLIP.firstCta.buttonLabel} &#8599;
        </p>
      </div>
    </m.div>
  );
}
